import { jwtDecode } from "jwt-decode";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import { z } from "zod";

export const AuthTokenSchema = z.object({
  id: z.number(),
  isSuperAdmin: z.coerce.boolean().default(false),
});

export interface AuthContextValue {
  lastURL: string | undefined;
  token: string | null;
  authInfo: z.infer<typeof AuthTokenSchema> | null;
  login: () => void;
  logout: () => void;
  signup: () => void;
  setLoginCredentials: (token: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const authGlobal = {
  currentContext: null as AuthContextValue | null,
};

export function getGlobalAuthContext() {
  return authGlobal.currentContext;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

function decodeToken(token: string | null): z.output<typeof AuthTokenSchema> | null {
  if (!token) return null;
  try {
    return AuthTokenSchema.parse(jwtDecode(token));
  } catch (err) {
    console.error("Error decoding token:", err);
    return null;
  }
}

export default function Auth({ children }: { children: ReactNode }): ReactNode {
  const navigate = useNavigate();
  const [lastURL, setLastURL] = useState<string | undefined>();

  const [token, setToken] = useState<string | null>(() => {
    const storedToken = window.localStorage ? window.localStorage.getItem("Authorization") : null;
    if (storedToken === "null" || !storedToken) {
      return null;
    }

    return storedToken;
  });

  const authInfo = useMemo(() => decodeToken(token), [token]);

  const logout = useCallback(() => {
    setToken(null);
    if (window.localStorage) {
      window.localStorage.removeItem("Authorization");
    }

    navigate("/");
  }, [navigate]);

  const login = useCallback(() => {
    setToken(null);
    setLastURL(window.location.pathname);
    navigate("/login");
  }, [navigate]);

  const signup = useCallback(() => {
    setToken(null);
    setLastURL(window.location.pathname);
    navigate("/signup");
  }, [navigate]);

  const setLoginCredentials = useCallback((token: string) => {
    setToken(token);
    if (window.localStorage) {
      window.localStorage.setItem("Authorization", token);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      lastURL,
      token,
      authInfo,
      login,
      logout,
      signup,
      setLoginCredentials,
    }),
    [lastURL, token, authInfo, login, logout, signup, setLoginCredentials],
  );

  authGlobal.currentContext = contextValue;

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
